import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'What is AI automation for small businesses?',
    answer: 'AI automation for small businesses uses artificial intelligence to handle repetitive, time-consuming tasks automatically — answering calls, following up with leads, scheduling appointments, responding to customer inquiries, sending review requests, and running marketing campaigns. It gives small businesses the operational power of a large company without the overhead of a large team.'
  },
  {
    question: 'Is AI automation too complex or expensive for a small business?',
    answer: 'Not at all — and this is one of the biggest misconceptions. Modern AI automation is specifically designed to be accessible for small businesses. Setup is handled for you, the systems run in the background without technical knowledge required, and the cost is a fraction of what it would take to hire staff to do the same tasks. Most clients see a positive ROI within the first 30–60 days.'
  },
  {
    question: 'What tasks can AI automation handle for my small business?',
    answer: 'A wide range: answering phone calls 24/7, responding to website chat inquiries, following up with new leads within 60 seconds, scheduling and confirming appointments, sending review requests after completed jobs, re-engaging past customers, running email and SMS marketing campaigns, handling common customer service questions, and posting to social media. Essentially any repetitive, rule-based task is a candidate for automation.'
  },
  {
    question: 'Will AI automation replace my staff?',
    answer: 'No — it frees your staff to focus on higher-value work. AI handles the repetitive, time-consuming tasks (answering the same questions, sending follow-ups, scheduling) so your team can focus on delivering great service, building customer relationships, and growing the business. Most businesses find that automation makes their existing team significantly more productive rather than replacing anyone.'
  },
  {
    question: 'How long does it take to set up AI automation?',
    answer: 'Most of our automation systems are fully set up and running within 48–72 hours. We handle the entire setup process — configuring the AI, connecting your existing tools, and testing everything before going live. You don\'t need any technical knowledge or to do anything beyond a brief onboarding call.'
  },
  {
    question: 'Will the AI sound robotic when talking to my customers?',
    answer: 'No. Our AI systems are trained to communicate in a natural, conversational tone that matches your brand voice. Customers regularly don\'t realize they\'re interacting with AI. The AI uses the customer\'s name, references their specific situation, and responds contextually — not with generic scripted responses.'
  },
  {
    question: 'What industries does AI automation work best for?',
    answer: 'AI automation works exceptionally well for any service-based small business: contractors, roofers, landscapers, HVAC companies, plumbers, electricians, cleaning services, pest control, auto repair, salons, medical and dental offices, real estate agents, insurance agencies, and more. If your business handles leads, appointments, and repeat customers, AI automation will have a significant impact.'
  },
  {
    question: 'How do I know if AI automation is working?',
    answer: 'You\'ll see it in your numbers. We provide clear reporting on leads responded to, appointments booked, reviews collected, and campaigns sent. Most clients notice the difference within the first week — faster response times, more booked appointments, and more Google reviews. We also do regular check-ins to review performance and optimize your automations over time.'
  }
];

const stats = [
  { value: '60 sec', label: 'Average AI response time to new leads' },
  { value: '40%', label: 'Average increase in booked appointments' },
  { value: '14 hrs', label: 'Per week saved on manual tasks' },
  { value: '3–5x', label: 'More Google reviews with automated requests' },
];

const features = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering',
    description: 'Never miss a call again. AI answers every call 24/7, qualifies leads, answers common questions, and books appointments — even at 2am on a Sunday.',
    link: '/ai-phone-answering-service'
  },
  {
    icon: 'ri-send-plane-line',
    title: 'Instant Lead Follow-Up',
    description: 'Every new lead gets a personalized response within 60 seconds — via text and email — automatically. No more leads going cold because you were busy.',
    link: '/services/ai-lead-follow-up'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Appointment Scheduling',
    description: 'AI books, confirms, and reminds customers about appointments automatically. Reduce no-shows and eliminate the back-and-forth of manual scheduling.',
    link: '/ai-appointment-scheduling'
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'Website AI Chatbot',
    description: 'Capture and qualify leads from your website 24/7. The chatbot answers questions, collects contact info, and books appointments while you sleep.',
    link: '/services/ai-chatbot'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Automated Customer Service',
    description: 'Handle 85% of customer inquiries automatically via chat, SMS, and email — without a single staff member involved.',
    link: '/services/automated-customer-service'
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Marketing Automation',
    description: 'Email drip campaigns, SMS follow-ups, review requests, and re-engagement campaigns — all running automatically in the background.',
    link: '/services/marketing-automation-small-business'
  },
  {
    icon: 'ri-star-line',
    title: 'Automated Review Requests',
    description: 'After every completed job, the system automatically asks happy customers for a Google review. Get 3–5x more reviews without lifting a finger.',
    link: '/services/marketing-automation-small-business'
  },
  {
    icon: 'ri-repeat-2-line',
    title: 'Customer Re-Engagement',
    description: 'Automatically reach out to past customers who haven\'t booked in 60, 90, or 180 days with personalized offers to bring them back.',
    link: '/services/marketing-automation-small-business'
  }
];

const industries = [
  { icon: 'ri-home-gear-line', name: 'HVAC & Plumbing' },
  { icon: 'ri-hammer-line', name: 'Contractors' },
  { icon: 'ri-building-line', name: 'Roofers' },
  { icon: 'ri-plant-line', name: 'Landscapers' },
  { icon: 'ri-car-line', name: 'Auto Repair' },
  { icon: 'ri-scissors-line', name: 'Salons & Spas' },
  { icon: 'ri-heart-pulse-line', name: 'Medical & Dental' },
  { icon: 'ri-home-smile-line', name: 'Cleaning Services' },
  { icon: 'ri-bug-line', name: 'Pest Control' },
];

const steps = [
  {
    number: '01',
    title: 'Discovery Call',
    description: 'We learn about your business, your biggest time drains, and where you\'re losing leads or customers. Takes about 30 minutes.'
  },
  {
    number: '02',
    title: 'Custom Automation Plan',
    description: 'We design a tailored automation stack for your specific business — the right tools for your industry, customer type, and goals.'
  },
  {
    number: '03',
    title: 'Setup & Integration',
    description: 'We handle the entire setup — connecting your phone, website, CRM, and marketing tools. You don\'t touch a single line of code.'
  },
  {
    number: '04',
    title: 'Go Live in 48 Hours',
    description: 'Your automations go live within 48–72 hours. We test everything thoroughly before launch so it works perfectly from day one.'
  },
  {
    number: '05',
    title: 'Ongoing Optimization',
    description: 'We monitor performance, review results with you monthly, and continuously optimize your automations to improve results over time.'
  }
];

export default function AIAutomationSmallBusiness() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI Automation for Small Businesses | Dino Nuggets"
        description="AI automation built for small businesses — answer calls, follow up leads, book appointments, collect reviews, and run marketing campaigns automatically. Save 14+ hours per week."
        canonical="/services/ai-automation-small-business"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Small Businesses',
              description: 'Complete AI automation solutions for small businesses including AI phone answering, lead follow-up, appointment scheduling, customer service automation, and marketing automation.',
              provider: {
                '@type': 'Organization',
                name: 'Dino Nuggets',
                url: import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'
              },
              areaServed: 'United States',
              availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: `${import.meta.env.VITE_SITE_URL || 'https://dinonuggets.ai'}/services/ai-automation-small-business`
              }
            }
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer }
              }))
            }
          }
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Spring Sale Banner */}
        <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white rounded-full translate-y-1/2"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🌱</span>
              <span className="font-bold text-base tracking-wide uppercase">Spring Sale</span>
              <span className="hidden sm:block w-px h-5 bg-white/40"></span>
            </div>
            <p className="text-sm sm:text-base font-medium text-white/95">
              <strong className="font-bold text-white">No setup fees</strong> on all automation packages through April 30th — save up to $500 on getting started.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="flex-shrink-0 bg-white text-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Offer
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/ai-auto-smb-hero-001.jpg"
              alt="AI Automation for Small Businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-robot-line"></i>
              Big business technology. Small business price.
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              AI Automation for Small Businesses
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Stop losing leads, missing calls, and doing everything manually. Our AI automation handles your phones, follow-ups, scheduling, customer service, and marketing — so you can focus on the work that actually grows your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Automate My Business
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See What Gets Automated
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* What Is It */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Is AI Automation for Small Businesses?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                AI automation for small businesses means using artificial intelligence to handle the repetitive, time-consuming tasks that eat up your day — <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">answering calls</Link>, <Link to="/services/ai-lead-follow-up" className="text-primary hover:underline font-medium">following up with leads</Link>, scheduling appointments, responding to customer questions, collecting reviews, and running marketing campaigns.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Until recently, this kind of technology was only accessible to large companies with big budgets and dedicated IT teams. That's changed. Today, small service businesses can deploy the same AI-powered systems that enterprise companies use — at a price point that makes sense for a small operation.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The result: your business operates like a well-staffed company even when it's just you and a small team. Leads get responded to instantly. Calls get answered 24/7. Appointments get booked automatically. Reviews get collected after every job. And <Link to="/services/marketing-automation-small-business" className="text-primary hover:underline font-medium">marketing campaigns</Link> run in the background without you touching them. It's not about replacing people — it's about making your existing team dramatically more effective.
              </p>
            </div>

            {/* How It Works */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How We Set Up Your AI Automation
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                From first call to fully automated in 48–72 hours:
              </p>
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <span className="text-primary font-bold text-lg">{step.number}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="absolute left-7 mt-14 w-0.5 h-6 bg-primary/20 hidden lg:block"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Everything That Gets Automated
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                A complete AI automation stack built for small service businesses:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <Link
                    key={feature.title}
                    to={feature.link}
                    className="border border-gray-200 p-6 rounded-lg hover:border-primary/40 transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                      <i className={`${feature.icon} text-xl text-primary`}></i>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors flex items-center gap-2">
                      {feature.title}
                      <i className="ri-arrow-right-line text-sm opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* The Real Cost of Doing It Manually */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Real Cost of Doing Everything Manually
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Most small business owners don't realize how much manual operations are costing them — not just in time, but in lost revenue from leads that went cold, calls that went unanswered, and customers who booked a competitor because they responded faster.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl border border-red-100">
                  <div className="text-3xl font-bold text-red-400 mb-2">47 hrs</div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">Average response time without automation</div>
                  <div className="text-xs text-gray-500 leading-relaxed">78% of customers book the first business that responds. If you're not first, you're losing jobs.</div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-amber-100">
                  <div className="text-3xl font-bold text-amber-400 mb-2">14 hrs</div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">Per week lost to manual admin tasks</div>
                  <div className="text-xs text-gray-500 leading-relaxed">That's 728 hours per year — nearly 18 full work weeks — spent on tasks AI can handle automatically.</div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-primary/20">
                  <div className="text-3xl font-bold text-primary mb-2">60 sec</div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">AI response time to every new lead</div>
                  <div className="text-xs text-gray-500 leading-relaxed">Respond in 60 seconds instead of 47 hours and convert 40% more leads into booked appointments.</div>
                </div>
              </div>
            </div>

            {/* Industries */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Built for Service-Based Small Businesses
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                AI automation works for any small business that handles leads, appointments, and repeat customers:
              </p>
              <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4">
                {industries.map((industry) => (
                  <div
                    key={industry.name}
                    className="flex flex-col items-center gap-3 p-5 border border-gray-200 rounded-xl hover:border-primary/40 transition-colors text-center"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl">
                      <i className={`${industry.icon} text-2xl text-primary`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{industry.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Explore Individual Automation Services
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Want to go deeper on a specific automation? Each service has its own dedicated page:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary transition-all cursor-pointer group"
                >
                  <i className="ri-send-plane-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    AI Lead Follow-Up
                  </h3>
                  <p className="text-sm text-gray-600">
                    Respond to every new lead in 60 seconds and nurture them to a booking automatically.
                  </p>
                </Link>
                <Link
                  to="/services/automated-customer-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary transition-all cursor-pointer group"
                >
                  <i className="ri-customer-service-2-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Automated Customer Service
                  </h3>
                  <p className="text-sm text-gray-600">
                    Handle 85% of customer inquiries automatically via chat, SMS, and email.
                  </p>
                </Link>
                <Link
                  to="/services/marketing-automation-small-business"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary transition-all cursor-pointer group"
                >
                  <i className="ri-mail-send-line text-3xl text-primary mb-3 block"></i>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    Marketing Automation
                  </h3>
                  <p className="text-sm text-gray-600">
                    Email, SMS, review requests, and re-engagement campaigns running on autopilot.
                  </p>
                </Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Ready to Run Your Business on Autopilot?
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Stop losing leads, missing calls, and spending hours on tasks AI can handle. Get your custom automation stack set up in 48 hours and start seeing results within 30 days.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="inline-block bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Book My Free Automation Audit
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
